import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import ListingCard from "../components/ListingCard";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Star, MapPin, Package, Gavel, Euro, Settings, LogOut } from "lucide-react";
import { listingsAPI } from "../lib/api";
import PushToggle from "../components/PushToggle";
import GdprPanel from "../components/GdprPanel";
import VerificationPanel from "../components/VerificationPanel";
import VerifiedBadge from "../components/VerifiedBadge";
import PromoteListingButton from "../components/PromoteListingButton";
import BusinessStatusPanel from "../components/BusinessStatusPanel";
import PayListingButton from "../components/PayListingButton";

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [myListings, setMyListings] = useState([]);

  useEffect(() => {
    if (user?.id) {
      listingsAPI.byUser(user.id).then(setMyListings).catch(() => setMyListings([]));
    }
  }, [user]);

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-bold text-[#1d3d3a]">Accedi per vedere il tuo profilo</h2>
        <Link to="/login" className="mt-4 inline-block"><Button className="bg-[#e86a24] hover:bg-[#c8561a]">Accedi</Button></Link>
      </div>
    );
  }

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
          <img src={user.avatar} alt={user.name} className="w-20 h-20 rounded-full border-4 border-white shadow"/>
          <div className="flex-1">
            <h1 className="text-2xl font-black text-[#1d3d3a] flex items-center gap-2">{user.name}{user.is_verified && <VerifiedBadge size="md" />}</h1>
            <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 mt-1">
              <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5"/>{user.castello || "San Marino"}</span>
              <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400"/>{Number(user.rating || 5).toFixed(1)} ({user.reviews_count || 0} recensioni)</span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline"><Settings className="w-4 h-4 mr-1"/>Impostazioni</Button>
            <Button variant="outline" onClick={handleLogout} className="text-red-600 border-orange-200 hover:bg-orange-50"><LogOut className="w-4 h-4 mr-1"/>Esci</Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3 mb-6">
        <StatCard icon={Package} value={myListings.length} label="Annunci attivi" color="text-[#e86a24] bg-orange-50"/>
        <StatCard icon={Gavel} value={0} label="Aste in corso" color="text-amber-600 bg-amber-50"/>
        <StatCard icon={Euro} value={0} label="Offerte ricevute" color="text-[#1d3d3a] bg-emerald-50"/>
        <StatCard icon={Star} value={myListings.filter(l => l.featured).length} label="Annunci VETRINA" color="text-amber-600 bg-amber-50"/>
      </div>

      <div className="mb-6">
        <BusinessStatusPanel />
        {user.account_type === "business" && (
          <Link to="/azienda/dashboard" className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-[#e86a24] hover:underline" data-testid="profile-go-to-business-dashboard">
            Apri Dashboard Azienda
            <span aria-hidden>→</span>
          </Link>
        )}
      </div>

      <div className="mb-6">
        <VerificationPanel />
      </div>

      <div className="mb-6">
        <PushToggle />
      </div>

      <div className="mb-6">
        <GdprPanel />
      </div>

      <Tabs defaultValue="annunci">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="annunci">Annunci</TabsTrigger>
          <TabsTrigger value="offerte">Offerte</TabsTrigger>
          <TabsTrigger value="aste">Aste</TabsTrigger>
          <TabsTrigger value="preferiti">Preferiti</TabsTrigger>
        </TabsList>
        <TabsContent value="annunci" className="mt-4">
          {myListings.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500">
              Non hai ancora pubblicato annunci.
              <div className="mt-4"><Link to="/pubblica"><Button className="bg-[#e86a24] hover:bg-[#c8561a]">Pubblica il primo annuncio</Button></Link></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {myListings.map(l => (
                <div key={l.id} className="space-y-2">
                  <div className="relative">
                    <ListingCard listing={l} />
                    {l.payment_required && !l.active && (
                      <div className="absolute inset-x-0 top-0 bg-amber-500/90 text-white text-[10px] font-black uppercase tracking-wider px-2 py-1 text-center" data-testid={`pending-listing-${l.id}`}>
                        In attesa pagamento €0,50
                      </div>
                    )}
                  </div>
                  <div className="flex justify-center gap-1.5 flex-wrap">
                    {l.payment_required && !l.active ? (
                      <PayListingButton listingId={l.id} />
                    ) : (
                      <PromoteListingButton listing={l} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="offerte" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500">
            Le tue offerte ricevute e inviate appariranno qui
          </div>
        </TabsContent>
        <TabsContent value="aste" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500">
            Le aste a cui partecipi o hai vinto
          </div>
        </TabsContent>
        <TabsContent value="preferiti" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500">
            Annunci che hai salvato nei preferiti
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ icon: Icon, value, label, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center mb-2`}><Icon className="w-5 h-5"/></div>
      <div className="text-2xl font-black text-[#1d3d3a]">{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}
